import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Banknote, CheckCircle2, Landmark } from "lucide-react";
import api from "@/lib/api";
import { LogoBig } from "@/components/Logo";

const eur = (n) => `${Number(n).toFixed(2).replace(".", ",")} €`;

export default function ReservationConfirmee() {
  const [params] = useSearchParams();
  const ref = params.get("ref");
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!ref) {
      setError(true);
      return;
    }
    api
      .get(`/track/${encodeURIComponent(ref)}`)
      .then(({ data }) => setBooking(data))
      .catch(() => setError(true));
  }, [ref]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-cream px-4 py-16">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-lg rounded-3xl bg-white p-8 text-center shadow-sm ring-1 ring-ink/5 sm:p-10"
        data-testid="reservation-confirmee"
      >
        <div className="mb-6 flex justify-center">
          <LogoBig />
        </div>

        {error ? (
          <>
            <h1 className="font-serif text-2xl font-semibold text-ink">Réservation introuvable</h1>
            <p className="mt-3 text-ink/60">Vérifiez votre référence ou appelez le 06 73 31 82 36.</p>
          </>
        ) : !booking ? (
          <p className="text-ink/60">Chargement…</p>
        ) : (
          <>
            <CheckCircle2 className="mx-auto mb-4 h-14 w-14 text-forest" />
            <h1 className="font-serif text-2xl font-semibold text-ink sm:text-3xl">Merci, c'est réservé !</h1>
            <p className="mt-3 text-ink/65">
              Votre réservation est enregistrée et un e-mail récapitulatif vient de vous être envoyé. Angélique vous
              appellera très vite pour confirmer le créneau et le tarif exact.
            </p>

            <div
              className="mt-6 flex items-center justify-center gap-3 rounded-2xl bg-terra/10 px-5 py-4 text-left"
              data-testid="payment-instruction"
            >
              {booking.payment_method === "virement" ? (
                <Landmark className="h-7 w-7 shrink-0 text-terra-dark" />
              ) : (
                <Banknote className="h-7 w-7 shrink-0 text-terra-dark" />
              )}
              <p className="text-sm leading-relaxed text-ink/75">
                {booking.payment_method === "virement" ? (
                  <>
                    <strong className="text-ink">Paiement par virement :</strong> Angélique vous communique ses
                    coordonnées bancaires par téléphone lors de la confirmation.
                  </>
                ) : (
                  <>
                    <strong className="text-ink">Paiement en espèces :</strong> préparez{" "}
                    <strong className="text-ink">{eur(booking.total)}</strong> pour le jour de la livraison.
                  </>
                )}
              </p>
            </div>

            <dl className="mt-6 space-y-3 rounded-2xl bg-cream p-6 text-left text-sm">
              <div className="flex justify-between">
                <dt className="text-ink/55">Référence</dt>
                <dd className="font-mono font-semibold text-ink" data-testid="booking-ref">{booking.ref}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-ink/55">Prestation</dt>
                <dd className="font-semibold text-ink">{booking.delivery_label}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-ink/55">Date</dt>
                <dd className="font-semibold text-ink">
                  {booking.date} — {booking.time_slot}
                </dd>
              </div>
              <div className="flex justify-between border-t border-ink/10 pt-3">
                <dt className="text-ink/55">Montant</dt>
                <dd className="font-serif text-lg font-semibold text-forest">{eur(booking.total)}</dd>
              </div>
            </dl>

            <Link
              to={`/suivi?ref=${booking.ref}`}
              data-testid="track-booking-link"
              className="mt-6 inline-flex min-h-[48px] items-center rounded-full bg-forest px-6 text-sm font-semibold text-cream transition-colors hover:bg-forest-dark"
            >
              Suivre ma livraison
            </Link>
          </>
        )}

        <div className="mt-4">
          <Link to="/" data-testid="back-home-link" className="text-sm font-medium text-ink/50 transition-colors hover:text-forest">
            ← Retour à l'accueil
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
